<template>
  <div class="quick-orders-card floating-widget">
    <!-- Widget Header -->
    <div class="card-header">
      <div class="card-title-group">
        <div class="title-icon-badge">
          <i class="fas fa-bolt"></i>
        </div>
        <div>
          <h2 class="card-title">Quick Orders</h2>
          <p class="card-subtitle">Create and track Bitwarden seat adjustments and YubiKey hardware orders</p>
        </div>
      </div>

      <div class="status-summary-pills">
        <span class="summary-pill pending-pill" @click="setFilterStatus('pending')" :class="{ active: statusFilter === 'pending' }">
          <i class="fas fa-clock"></i> {{ counts.pendingCount }} Pending
        </span>
        <span class="summary-pill completed-pill" @click="setFilterStatus('completed')" :class="{ active: statusFilter === 'completed' }">
          <i class="fas fa-check-circle"></i> {{ counts.completedCount }} Completed
        </span>
        <button type="button" class="btn-header-create" @click="openCreateModal('bitwarden_seat_change')">
          <i class="fas fa-plus"></i> Create Request
        </button>
      </div>
    </div>

    <!-- Reminder List Toolbar / Filters -->
    <div class="list-toolbar">
      <div class="toolbar-left">
        <span class="toolbar-section-label">
          <i class="fas fa-list-check me-1"></i> Order Reminders
        </span>
        <div class="status-tabs">
          <button
            type="button"
            class="status-tab-btn"
            :class="{ active: statusFilter === 'pending' }"
            @click="setFilterStatus('pending')"
          >
            Pending ({{ counts.pendingCount }})
          </button>
          <button
            type="button"
            class="status-tab-btn"
            :class="{ active: statusFilter === 'completed' }"
            @click="setFilterStatus('completed')"
          >
            Completed ({{ counts.completedCount }})
          </button>
          <button
            type="button"
            class="status-tab-btn"
            :class="{ active: statusFilter === 'all' }"
            @click="setFilterStatus('all')"
          >
            All
          </button>
        </div>
      </div>

      <div class="controls">
        <div class="control-group">
          <label for="type-filter-orders">Type:</label>
          <select id="type-filter-orders" v-model="orderTypeFilter" class="form-select" @change="onFilterChange">
            <option value="all">All Types</option>
            <option value="bitwarden_seat_change">Bitwarden</option>
            <option value="yubikey_order">YubiKey</option>
          </select>
        </div>

        <div class="control-group">
          <label for="sort-by-orders">Sort By:</label>
          <select id="sort-by-orders" v-model="sortBy" class="form-select" @change="onFilterChange">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        <div class="control-group">
          <label for="rows-per-page-orders">Rows per page:</label>
          <select id="rows-per-page-orders" v-model.number="pagination.pageSize" class="form-select" @change="onPageSizeChange">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
          </select>
        </div>

        <div class="pagination">
          <button type="button" @click="prevPage" :disabled="pagination.currentPage === 1" class="pagination-arrow">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="pagination-label">
            <strong>{{ paginationRange }}</strong> of {{ pagination.totalItems }}
          </span>
          <button type="button" @click="nextPage" :disabled="pagination.currentPage >= totalPages" class="pagination-arrow">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Orders Reminder List -->
    <div class="orders-list">
      <div v-if="isLoading" class="loading-state">
        <i class="fas fa-spinner fa-spin me-2"></i> Loading quick orders...
      </div>
      <div v-else-if="orders.length === 0" class="empty-state">
        <i class="fas fa-clipboard-check empty-icon"></i>
        <p class="empty-title">No {{ statusFilter === 'all' ? '' : statusFilter }} quick orders found.</p>
        <p class="empty-sub">Click "+ Create Request" above to create a Bitwarden seat change or YubiKey order request.</p>
      </div>
      <div
        v-else
        v-for="order in orders"
        :key="order.id"
        class="order-row"
        :class="{ 'is-completed': order.status === 'completed' }"
      >
        <div class="order-info-wrapper">
          <div class="order-date">
            <div class="date-formatted">{{ formatDate(order.created_at) }}</div>
            <div class="time-formatted">{{ formatTime(order.created_at) }}</div>
          </div>

          <div class="order-info">
            <div class="order-top-line">
              <span
                class="order-type-pill"
                :class="order.order_type === 'bitwarden_seat_change' ? 'type-bitwarden' : 'type-yubikey'"
              >
                <i :class="order.order_type === 'bitwarden_seat_change' ? 'fas fa-shield-alt' : 'fas fa-key'"></i>
                {{ formatOrderType(order.order_type) }}
              </span>

              <span class="org-name">{{ order.organisation_name }}</span>

              <span class="action-qty-pill" :class="getActionPillClass(order)">
                {{ order.action_detail }} &bull;
                {{ order.order_type === 'bitwarden_seat_change' ? `${order.quantity} ${order.quantity === 1 ? 'seat' : 'seats'}` : `Qty: ${order.quantity}` }}
              </span>

              <span class="status-badge" :class="order.status === 'completed' ? 'badge-completed' : 'badge-pending'">
                <i :class="order.status === 'completed' ? 'fas fa-check' : 'fas fa-hourglass-half'"></i>
                {{ order.status === 'completed' ? 'Completed' : 'Pending' }}
              </span>
            </div>

            <div v-if="order.notes" class="order-notes">
              {{ order.notes }}
            </div>

            <div class="order-meta">
              <span>
                <i class="far fa-user me-1"></i>
                Requested by <strong>{{ order.created_by_name || order.created_by_email || 'Spark User' }}</strong>
              </span>
              <span v-if="order.status === 'completed' && (order.completed_by_name || order.completed_at)" class="completed-meta">
                &bull; Completed
                <template v-if="order.completed_by_name">by <strong>{{ order.completed_by_name }}</strong></template>
                <template v-if="order.completed_at"> on {{ formatDate(order.completed_at) }}</template>
              </span>
            </div>
          </div>
        </div>

        <div class="order-actions">
          <button
            v-if="order.status !== 'completed'"
            type="button"
            class="action-btn-circle action-btn-circle--complete"
            :disabled="updatingId === order.id"
            @click="toggleOrderStatus(order, 'completed')"
            title="Mark as Completed"
          >
            <i :class="updatingId === order.id ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
          </button>
          <button
            v-else
            type="button"
            class="action-btn-circle action-btn-circle--reopen"
            :disabled="updatingId === order.id"
            @click="toggleOrderStatus(order, 'pending')"
            title="Reopen as Pending"
          >
            <i :class="updatingId === order.id ? 'fas fa-spinner fa-spin' : 'fas fa-rotate-left'"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Option A3: Quick Order Creation Modal -->
  <Teleport to="body">
    <div v-if="isModalOpen" class="modal-backdrop-custom" @click="closeModalOnBackdrop">
      <div class="modal-content-custom" @click.stop>
        <div class="modal-header-custom">
          <div class="modal-header-title-wrap">
            <div class="modal-icon-circle">
              <i :class="form.order_type === 'bitwarden_seat_change' ? 'fas fa-shield-alt' : 'fas fa-key'"></i>
            </div>
            <div>
              <h5 class="modal-title-custom">
                {{ form.order_type === 'bitwarden_seat_change' ? 'New Bitwarden Seat Change Request' : 'New Yubikey Order Request' }}
              </h5>
              <p class="modal-subtitle-custom">Submit an order reminder for the operations &amp; provisioning team</p>
            </div>
          </div>
          <button type="button" class="close-button-custom" @click="closeOrderModal">&times;</button>
        </div>

        <!-- Request Type Switcher Tabs -->
        <div class="modal-type-switcher">
          <button
            type="button"
            class="type-switch-btn"
            :class="{ active: form.order_type === 'bitwarden_seat_change' }"
            @click="switchOrderType('bitwarden_seat_change')"
          >
            <i class="fas fa-shield-alt"></i> Bitwarden Seat Change
          </button>
          <button
            type="button"
            class="type-switch-btn"
            :class="{ active: form.order_type === 'yubikey_order' }"
            @click="switchOrderType('yubikey_order')"
          >
            <i class="fas fa-key"></i> Yubikey Order
          </button>
        </div>

        <form @submit.prevent="submitQuickOrder" class="modal-form-body">
          <!-- Client / Organisation Selection -->
          <div class="form-group-vertex">
            <div class="label-row">
              <label class="vertex-label">Client / Organisation <span class="required-star">*</span></label>
              <button
                v-if="leadsList.length > 0"
                type="button"
                class="btn-toggle-custom-org"
                @click="toggleCustomOrgMode"
              >
                {{ useCustomOrg ? 'Select from existing clients' : 'Enter custom organisation name' }}
              </button>
            </div>

            <template v-if="!useCustomOrg && leadsList.length > 0">
              <select
                v-model="selectedLeadId"
                class="vertex-input"
                @change="onLeadSelectChange"
                required
              >
                <option :value="null" disabled>-- Select a Client Organisation --</option>
                <option v-for="lead in leadsList" :key="lead.id" :value="lead.id">
                  {{ getLeadOrgName(lead) }}{{ lead.first_name || lead.last_name ? ` (${[lead.first_name, lead.last_name].filter(Boolean).join(' ')})` : '' }}
                </option>
              </select>
            </template>
            <template v-else>
              <input
                type="text"
                v-model.trim="form.organisation_name"
                class="vertex-input"
                placeholder="Enter client or organisation name (e.g. Acme Corporation)"
                maxlength="255"
                required
              />
            </template>
          </div>

          <!-- Action / Model Row + Quantity -->
          <div class="form-row-two-col">
            <div class="form-group-vertex">
              <label class="vertex-label">
                {{ form.order_type === 'bitwarden_seat_change' ? 'Seat Adjustment Action' : 'YubiKey Model' }}
                <span class="required-star">*</span>
              </label>

              <div v-if="form.order_type === 'bitwarden_seat_change'" class="segmented-action-group">
                <button
                  type="button"
                  class="segment-btn"
                  :class="{ active: form.action_detail === 'Add Seats' }"
                  @click="form.action_detail = 'Add Seats'"
                >
                  <i class="fas fa-plus-circle"></i> Add Seats
                </button>
                <button
                  type="button"
                  class="segment-btn remove-segment"
                  :class="{ active: form.action_detail === 'Remove Seats' }"
                  @click="form.action_detail = 'Remove Seats'"
                >
                  <i class="fas fa-minus-circle"></i> Remove Seats
                </button>
              </div>

              <select
                v-else
                v-model="form.action_detail"
                class="vertex-input"
                required
              >
                <option v-for="model in yubikeyModels" :key="model" :value="model">
                  {{ model }}
                </option>
              </select>
            </div>

            <div class="form-group-vertex">
              <label class="vertex-label">
                {{ form.order_type === 'bitwarden_seat_change' ? 'Number of Seats' : 'Quantity' }}
                <span class="required-star">*</span>
              </label>
              <input
                type="number"
                v-model.number="form.quantity"
                class="vertex-input"
                min="1"
                max="10000"
                step="1"
                required
              />
            </div>
          </div>

          <!-- Notes / Delivery Details -->
          <div class="form-group-vertex">
            <label class="vertex-label">
              {{ form.order_type === 'bitwarden_seat_change' ? 'Notes / Billing Reference' : 'Delivery Address &amp; Recipient Notes' }}
            </label>
            <textarea
              v-model="form.notes"
              class="vertex-textarea"
              rows="3"
              maxlength="2000"
              :placeholder="
                form.order_type === 'bitwarden_seat_change'
                  ? 'Optional notes, effective date, or ticket/billing reference...'
                  : 'Include recipient contact name, shipping address, phone number, or special instructions...'
              "
            ></textarea>
            <p v-if="hasDangerousChars" class="xss-warning-text">
              <i class="fas fa-exclamation-triangle me-1"></i>
              HTML angle brackets (&lt; or &gt;) are not permitted and will be stripped.
            </p>
          </div>

          <div v-if="formError" class="form-error-banner">
            <i class="fas fa-circle-exclamation me-2"></i> {{ formError }}
          </div>

          <!-- Modal Footer Actions -->
          <div class="modal-footer-custom">
            <button type="button" class="btn-vertex-secondary" @click="closeOrderModal" :disabled="isSubmitting">
              Cancel
            </button>
            <button type="submit" class="btn-vertex-primary" :disabled="isSubmitting || hasDangerousChars">
              <i :class="isSubmitting ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
              <span>{{ isSubmitting ? 'Submitting Request...' : 'Submit Quick Order' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: 'QuickOrdersWidget',
  emits: ['count-updated'],
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      updatingId: null,
      orders: [],
      statusFilter: 'pending',
      orderTypeFilter: 'all',
      sortBy: 'newest',
      pagination: {
        currentPage: 1,
        pageSize: 5,
        totalItems: 0,
      },
      counts: {
        pendingCount: 0,
        completedCount: 0,
        totalItems: 0,
      },
      isModalOpen: false,
      leadsList: [],
      leadsLoaded: false,
      useCustomOrg: false,
      selectedLeadId: null,
      formError: '',
      yubikeyModels: [
        'YubiKey 5 NFC',
        'YubiKey 5C NFC',
        'YubiKey 5 Nano',
        'YubiKey 5C Nano',
        'Security Key NFC',
        'Security Key C NFC',
      ],
      form: {
        order_type: 'bitwarden_seat_change',
        lead_id: null,
        organisation_name: '',
        action_detail: 'Add Seats',
        quantity: 1,
        notes: '',
      },
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.pagination.totalItems / this.pagination.pageSize));
    },
    paginationRange() {
      const total = this.pagination.totalItems;
      if (total === 0) return '0';
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
      const end = Math.min(start + this.pagination.pageSize - 1, total);
      return `${start}-${end}`;
    },
    hasDangerousChars() {
      const checkStr = `${this.form.organisation_name || ''} ${this.form.notes || ''}`;
      return /[<>]/.test(checkStr);
    },
  },
  created() {
    this.fetchQuickOrders();
    this.fetchLeads();
    this.fetchYubikeyModels();
  },
  methods: {
    async fetchYubikeyModels() {
      try {
        const res = await this.$axios.get('/categories?name=YubikeyModels');
        const category = res?.data?.category;
        if (category?.subcategories?.length > 0) {
          const models = category.subcategories
            .map((s) => (s.display_name || s.name || '').trim())
            .filter(Boolean);
          if (models.length > 0) {
            this.yubikeyModels = models;
          }
          return;
        }
        if (!category?.id) {
          const groupsRes = await this.$axios.get('/groups?pageSize=50&page=1');
          const groups = groupsRes?.data?.groups || [];
          const sparkGroup = groups.find(
            (g) =>
              g.name?.toLowerCase().includes('spark') ||
              g.display_name?.toLowerCase().includes('spark') ||
              g.name?.toLowerCase().includes('dropdown') ||
              g.display_name?.toLowerCase().includes('dropdown') ||
              (Array.isArray(g.categories) &&
                g.categories.some((c) => c.name === 'LeadType' || c.name === 'Products'))
          );
          if (sparkGroup?.id) {
            const createRes = await this.$axios.post('/categories', {
              group_id: sparkGroup.id,
              name: 'YubikeyModels',
              display_name: 'Yubikey',
              subcategories: [
                { name: 'YubiKey_5_NFC', display_name: 'YubiKey 5 NFC' },
                { name: 'YubiKey_5C_NFC', display_name: 'YubiKey 5C NFC' },
                { name: 'YubiKey_5_Nano', display_name: 'YubiKey 5 Nano' },
                { name: 'YubiKey_5C_Nano', display_name: 'YubiKey 5C Nano' },
                { name: 'Security_Key_NFC', display_name: 'Security Key NFC' },
                { name: 'Security_Key_C_NFC', display_name: 'Security Key C NFC' },
              ],
            });
            const createdSubs = createRes?.data?.category?.subcategories;
            if (Array.isArray(createdSubs) && createdSubs.length > 0) {
              this.yubikeyModels = createdSubs
                .map((s) => (s.display_name || s.name || '').trim())
                .filter(Boolean);
            }
          }
        }
      } catch (err) {
        // Silently fall back to default yubikeyModels list
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: '2-digit' };
      return date.toLocaleDateString('en-US', options);
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    formatOrderType(orderType) {
      return orderType === 'bitwarden_seat_change' ? 'Bitwarden Seat Change' : 'Yubikey Order';
    },
    getActionPillClass(order) {
      if (order.order_type === 'bitwarden_seat_change') {
        return order.action_detail === 'Remove Seats' ? 'pill-remove' : 'pill-add';
      }
      return 'pill-hardware';
    },
    getLeadOrgName(lead) {
      if (!lead) return '';
      return (lead.auth_organisation_name || lead.organisation_name || '').trim();
    },
    async fetchLeads() {
      if (this.leadsLoaded) return;
      try {
        const response = await this.$axios.get('/leads');
        const rawLeads =
          response?.data?.data ||
          response?.data?.leads ||
          (Array.isArray(response?.data) ? response.data : []);
        if (Array.isArray(rawLeads)) {
          const validLeads = rawLeads.filter((l) => this.getLeadOrgName(l).length > 0);
          validLeads.sort((a, b) => this.getLeadOrgName(a).localeCompare(this.getLeadOrgName(b)));
          this.leadsList = validLeads;
          this.leadsLoaded = true;
          if (this.leadsList.length === 0) {
            this.useCustomOrg = true;
          }
        } else {
          this.useCustomOrg = true;
        }
      } catch (err) {
        this.useCustomOrg = true;
      }
    },
    async fetchQuickOrders() {
      this.isLoading = true;
      try {
        const params = {
          status: this.statusFilter,
          sort: this.sortBy,
          page: this.pagination.currentPage,
          limit: this.pagination.pageSize,
        };
        if (this.orderTypeFilter !== 'all') {
          params.order_type = this.orderTypeFilter;
        }
        const response = await this.$axios.get('/api/v1/quick-orders', { params });
        if (response.data && (response.data.success || response.data.status)) {
          const rawItems = response.data.data || [];
          const items =
            this.orderTypeFilter !== 'all'
              ? rawItems.filter((o) => o.order_type === this.orderTypeFilter)
              : rawItems;
          this.orders = items;
          this.pagination.totalItems =
            items.length !== rawItems.length
              ? items.length
              : (response.data.total_items ?? this.orders.length);
          this.counts.pendingCount = response.data.pending_count ?? 0;
          this.counts.completedCount = response.data.completed_count ?? 0;
          this.counts.totalItems = response.data.total_items ?? 0;
          this.$emit('count-updated', { ...this.counts });
        }
      } catch (error) {
        console.error('Failed to fetch quick orders:', error);
      } finally {
        this.isLoading = false;
      }
    },
    setFilterStatus(status) {
      if (this.statusFilter === status) return;
      this.statusFilter = status;
      this.pagination.currentPage = 1;
      this.fetchQuickOrders();
    },
    onFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchQuickOrders();
    },
    onPageSizeChange() {
      this.pagination.currentPage = 1;
      this.fetchQuickOrders();
    },
    prevPage() {
      if (this.pagination.currentPage > 1) {
        this.pagination.currentPage--;
        this.fetchQuickOrders();
      }
    },
    nextPage() {
      if (this.pagination.currentPage < this.totalPages) {
        this.pagination.currentPage++;
        this.fetchQuickOrders();
      }
    },
    openCreateModal(orderType = 'bitwarden_seat_change') {
      this.formError = '';
      this.form.order_type = orderType === 'yubikey_order' ? 'yubikey_order' : 'bitwarden_seat_change';
      this.form.action_detail =
        this.form.order_type === 'bitwarden_seat_change'
          ? 'Add Seats'
          : this.yubikeyModels[0] || 'YubiKey 5 NFC';
      this.form.quantity = 1;
      this.form.notes = '';
      this.selectedLeadId = null;
      this.form.lead_id = null;
      this.form.organisation_name = '';
      this.useCustomOrg = this.leadsList.length === 0;
      this.isModalOpen = true;
      if (!this.leadsLoaded) {
        this.fetchLeads();
      }
    },
    openOrderModal(orderType = 'bitwarden_seat_change') {
      this.openCreateModal(orderType);
    },
    switchOrderType(orderType) {
      if (this.form.order_type === orderType) return;
      this.form.order_type = orderType;
      this.form.action_detail =
        orderType === 'bitwarden_seat_change'
          ? 'Add Seats'
          : this.yubikeyModels[0] || 'YubiKey 5 NFC';
      this.formError = '';
    },
    toggleCustomOrgMode() {
      this.useCustomOrg = !this.useCustomOrg;
      this.selectedLeadId = null;
      this.form.lead_id = null;
      this.form.organisation_name = '';
    },
    parseLeadId(val) {
      if (val === null || val === undefined || String(val).trim() === '') return null;
      const num = Number(val);
      if (!isNaN(num) && Number.isInteger(num) && num > 0) {
        return num;
      }
      return String(val).trim();
    },
    onLeadSelectChange() {
      const found = this.leadsList.find(
        (l) => l.id === this.selectedLeadId || String(l.id) === String(this.selectedLeadId)
      );
      if (found) {
        this.form.lead_id = this.parseLeadId(found.id);
        this.form.organisation_name = this.getLeadOrgName(found);
      } else {
        this.form.lead_id = this.parseLeadId(this.selectedLeadId);
        this.form.organisation_name = '';
      }
    },
    closeOrderModal() {
      if (this.isSubmitting) return;
      this.isModalOpen = false;
      this.formError = '';
    },
    closeModalOnBackdrop(event) {
      if (event.target.classList.contains('modal-backdrop-custom')) {
        this.closeOrderModal();
      }
    },
    getCurrentUserIdentity() {
      const currentUser = this.$store?.state?.auth?.currentUser || {};
      const fullName =
        currentUser.name ||
        [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') ||
        currentUser.username ||
        '';
      const email = currentUser.email || '';
      return { name: fullName, email };
    },
    async submitQuickOrder() {
      if (this.isSubmitting) return;
      this.formError = '';

      if (!this.useCustomOrg && this.selectedLeadId !== null && this.selectedLeadId !== '') {
        const found = this.leadsList.find(
          (l) => l.id === this.selectedLeadId || String(l.id) === String(this.selectedLeadId)
        );
        if (found) {
          this.form.lead_id = this.parseLeadId(found.id);
          this.form.organisation_name = this.getLeadOrgName(found);
        } else {
          this.form.lead_id = this.parseLeadId(this.selectedLeadId);
        }
      }

      const orgName = (this.form.organisation_name || '').trim();
      if (!orgName) {
        this.formError = 'Please select or enter a Client / Organisation name.';
        return;
      }

      const qty = Number(this.form.quantity);
      if (!Number.isInteger(qty) || qty < 1 || qty > 10000) {
        this.formError = 'Quantity must be a whole number between 1 and 10,000.';
        return;
      }

      if (this.hasDangerousChars) {
        this.formError = 'Please remove angle brackets (< or >) before submitting.';
        return;
      }

      this.isSubmitting = true;
      try {
        const userIdentity = this.getCurrentUserIdentity();
        const normalizedLeadId = this.useCustomOrg
          ? null
          : this.parseLeadId(this.form.lead_id ?? this.selectedLeadId);
        const payload = {
          order_type: this.form.order_type,
          lead_id: normalizedLeadId,
          organisation_name: orgName,
          action_detail: this.form.action_detail,
          quantity: Number(this.form.quantity),
          notes: (this.form.notes || '').trim(),
          created_by_name: userIdentity.name,
          created_by_email: userIdentity.email,
        };

        const response = await this.$axios.post('/api/v1/quick-orders', payload);
        if (response.data && (response.data.success || response.data.status)) {
          if (this.$toast) {
            this.$toast.success('Quick order request created successfully.');
          }
          this.isModalOpen = false;
          this.statusFilter = 'pending';
          this.pagination.currentPage = 1;
          await this.fetchQuickOrders();
        } else {
          this.formError = response.data?.message || 'Failed to create quick order.';
        }
      } catch (error) {
        this.formError =
          error.response?.data?.message || 'An error occurred while creating the quick order.';
      } finally {
        this.isSubmitting = false;
      }
    },
    async toggleOrderStatus(order, targetStatus) {
      if (this.updatingId === order.id) return;
      this.updatingId = order.id;
      try {
        const userIdentity = this.getCurrentUserIdentity();
        const response = await this.$axios.put(`/api/v1/quick-orders/${order.id}/status`, {
          status: targetStatus,
          completed_by_name: userIdentity.name || userIdentity.email,
        });
        if (response.data && (response.data.success || response.data.status)) {
          if (this.$toast) {
            this.$toast.success(
              targetStatus === 'completed'
                ? 'Order marked as completed.'
                : 'Order reopened as pending.'
            );
          }
          await this.fetchQuickOrders();
        } else if (this.$toast) {
          this.$toast.error(response.data?.message || 'Failed to update order status.');
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error('An error occurred while updating order status.');
        }
      } finally {
        this.updatingId = null;
      }
    },
  },
};
</script>

<style scoped>
.quick-orders-card {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  padding: 0;
  font-family: 'Roboto', sans-serif;
  width: 100%;
  max-width: 100%;
}

.floating-widget {
  position: relative;
  width: 100%;
  z-index: 10;
}

/* Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.title-icon-badge {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background-color: #DAF3F9;
  color: #00A0C1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #435666;
  margin: 0;
}

.card-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #5C768B;
}

.status-summary-pills {
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.pending-pill {
  background-color: #FFDEC3;
  color: #F3994D;
}

.pending-pill.active {
  border-color: #F3994D;
  box-shadow: 0 2px 6px rgba(243, 153, 77, 0.25);
}

.completed-pill {
  background-color: #D1FAE5;
  color: #00A54F;
}

.completed-pill.active {
  border-color: #00A54F;
  box-shadow: 0 2px 6px rgba(0, 165, 79, 0.2);
}

.btn-header-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #00A0C1, #33b5d1);
  background-color: #00A0C1;
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
  transition: all 0.2s ease;
}

.btn-header-create:hover {
  background: linear-gradient(135deg, #008da8, #00A0C1);
  background-color: #008da8;
  box-shadow: 0 4px 14px rgba(0, 160, 193, 0.4);
  transform: translateY(-1px);
}

/* List Toolbar */
.list-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-section-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #435666;
}

.status-tabs {
  display: inline-flex;
  background-color: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 4px;
}

.status-tab-btn {
  border: none;
  background: transparent;
  color: #5C768B;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.status-tab-btn.active {
  background-color: #ffffff;
  color: #00A0C1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-group label {
  color: #5C768B;
  font-size: 0.88rem;
  font-weight: 500;
  white-space: nowrap;
}

.form-select {
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px 12px;
  padding: 6px 36px 6px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #435666;
  background-color: #fff;
}

/* Pagination */
.pagination {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
}

.pagination-label {
  font-size: 0.88rem;
  color: #5C768B;
  font-weight: 500;
  padding: 0 8px;
  white-space: nowrap;
}

.pagination-label strong {
  color: #435666;
}

.pagination-arrow {
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #00A0C1;
  font-size: 0.95rem;
  padding: 6px 12px;
  transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
  background-color: #f1f3f5;
}

.pagination-arrow:disabled {
  color: #ced4da;
  cursor: not-allowed;
}

/* Orders Reminder List */
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px 24px;
}

.order-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.order-row:hover {
  border-color: #00A0C1;
  box-shadow: 0 3px 10px rgba(0, 160, 193, 0.08);
}

.order-row.is-completed {
  background-color: #fafbfc;
  opacity: 0.88;
}

.order-info-wrapper {
  display: flex;
  align-items: stretch;
  flex-grow: 1;
  min-width: 0;
}

.order-date {
  background-color: #435666;
  color: #ffffff;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 12px;
  text-align: center;
  flex-shrink: 0;
}

.date-formatted {
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.time-formatted {
  font-size: 0.78rem;
  color: #dbe4ec;
  margin-top: 2px;
}

.order-info {
  flex-grow: 1;
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.order-top-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.order-type-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.type-bitwarden {
  background-color: #DAF3F9;
  color: #00A0C1;
}

.type-yubikey {
  background-color: #edf2f7;
  color: #435666;
}

.org-name {
  font-weight: 700;
  font-size: 1.05rem;
  color: #435666;
}

.action-qty-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.pill-add {
  background-color: #e6f6f9;
  color: #00829e;
  border: 1px solid #b6e6f2;
}

.pill-remove {
  background-color: #fff3e6;
  color: #d97724;
  border: 1px solid #ffd5b0;
}

.pill-hardware {
  background-color: #f1f5f9;
  color: #435666;
  border: 1px solid #cbd5e1;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.74rem;
  padding: 3px 10px;
  border-radius: 9999px;
  font-weight: 700;
  text-transform: uppercase;
}

.badge-pending {
  background-color: #FFDEC3;
  color: #F3994D;
}

.badge-completed {
  background-color: #D1FAE5;
  color: #00A54F;
}

.order-notes {
  font-size: 0.9rem;
  color: #435666;
  background-color: #f8fafc;
  border-left: 3px solid #00A0C1;
  padding: 6px 10px;
  border-radius: 0 6px 6px 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.order-meta {
  font-size: 0.8rem;
  color: #5C768B;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.completed-meta {
  color: #00A54F;
}

.order-actions {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  flex-shrink: 0;
}

.action-btn-circle {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  padding: 0;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  position: relative;
}

.action-btn-circle--complete {
  background-color: #e6f7fa;
  color: #00A0C1;
  border: none;
}

.action-btn-circle--complete:hover:not(:disabled) {
  background-color: #00A0C1;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.action-btn-circle--reopen {
  background-color: #f1f5f9;
  color: #475569;
  border: none;
}

.action-btn-circle--reopen:hover:not(:disabled) {
  background-color: #475569;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(71, 85, 105, 0.25);
}

.action-btn-circle:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Empty & Loading States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 36px 20px;
  color: #5C768B;
}

.empty-icon {
  font-size: 2rem;
  color: #cbd5e1;
  margin-bottom: 8px;
}

.empty-title {
  font-weight: 600;
  color: #435666;
  margin: 0 0 4px 0;
}

.empty-sub {
  font-size: 0.85rem;
  color: #5C768B;
  margin: 0;
}

/* Modal Styles (Option A3) */
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(29, 43, 54, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 16px;
}

.modal-content-custom {
  position: relative;
  background-color: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}

.modal-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header-title-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: #DAF3F9;
  color: #00A0C1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.modal-title-custom {
  font-weight: 700;
  font-size: 1.15rem;
  color: #435666;
  margin: 0;
}

.modal-subtitle-custom {
  font-size: 0.82rem;
  color: #5C768B;
  margin: 2px 0 0 0;
}

.close-button-custom {
  font-size: 1.8rem;
  line-height: 1;
  color: #5C768B;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
}

.close-button-custom:hover {
  color: #435666;
}

.modal-type-switcher {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 12px 24px;
  background-color: #F8FAFC;
  border-bottom: 1px solid #e9ecef;
}

.type-switch-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #dbe4ec;
  background-color: #ffffff;
  color: #5C768B;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.type-switch-btn.active {
  background-color: #DAF3F9;
  color: #00A0C1;
  border-color: #00A0C1;
  font-weight: 700;
}

.modal-form-body {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group-vertex {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.vertex-label {
  font-size: 0.86rem;
  font-weight: 700;
  color: #435666;
  margin: 0;
}

.required-star {
  color: #e53e3e;
}

.btn-toggle-custom-org {
  background: none;
  border: none;
  color: #00A0C1;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.btn-toggle-custom-org:hover {
  color: #15B7D9;
}

.vertex-input,
.vertex-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  color: #435666;
  background-color: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.vertex-input:focus,
.vertex-textarea:focus {
  outline: none;
  border-color: #00A0C1;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.form-row-two-col {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
}

.segmented-action-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.segment-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  color: #5C768B;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.segment-btn.active {
  background-color: #DAF3F9;
  color: #00A0C1;
  border-color: #00A0C1;
  font-weight: 700;
}

.segment-btn.remove-segment.active {
  background-color: #FFDEC3;
  color: #d97724;
  border-color: #F3994D;
}

.xss-warning-text {
  font-size: 0.78rem;
  color: #d97724;
  margin: 2px 0 0 0;
}

.form-error-banner {
  background-color: #fff5f5;
  color: #c53030;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  font-weight: 500;
}

.modal-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px solid #e9ecef;
}

.btn-vertex-secondary {
  background-color: #f1f5f9;
  color: #435666;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-vertex-secondary:hover:not(:disabled) {
  background-color: #e2e8f0;
}

.btn-vertex-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00A0C1, #33b5d1);
  background-color: #00A0C1;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
  transition: all 0.2s ease;
}

.btn-vertex-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #008da8, #00A0C1);
  background-color: #008da8;
  box-shadow: 0 4px 14px rgba(0, 160, 193, 0.4);
  transform: translateY(-1px);
}

.btn-vertex-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .form-row-two-col {
    grid-template-columns: 1fr;
  }
  .order-row {
    flex-direction: column;
    align-items: stretch;
  }
  .order-actions {
    justify-content: flex-end;
    border-top: 1px solid #f1f5f9;
  }
}
</style>
